import { useState, useEffect } from 'react';
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    contact: '',
    password: '',
    contactType: 'email' // 'email' ou 'phone'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleContactTypeChange = (type) => {
    setFormData({
      ...formData,
      contactType: type,
      contact: ''
    });
  };

  const resetForm = () => {
    setFormData({
      contact: '',
      password: '',
      contactType: 'email'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (!formData.contact.trim() || !formData.password.trim()) {
      setError('Tous les champs sont obligatoires');
      return;
    }
    
    setLoading(true);
  
    try {
      const userData = await login(formData.contact, formData.password);
      
      resetForm();
      
      if (userData.user.is_admin) {
        navigate('/admin-dashboard');
      } else if (userData.user.is_farmer) {
        navigate('/farmer-dashboard');
      } else if (userData.user.is_customer) {
        navigate('/products');
      } else {
        navigate('/');
      }
    } catch (err) {
      let errorMessage = 'Échec de la connexion';
      
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        
        if (errorData.detail && errorData.detail.includes('Email/téléphone ou mot de passe incorrect')) {
          errorMessage = 'Email/téléphone ou mot de passe incorrect. Veuillez vérifier vos informations.';
        } else if (errorData.detail && errorData.detail.includes('Compte désactivé')) {
          errorMessage = 'Votre compte est désactivé. Veuillez vérifier votre email pour l\'activer.';
        } else {
          errorMessage = errorData.detail || 'Une erreur est survenue lors de la connexion';
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-slate mb-2 text-center">Connexion</h1>

        <p className="text-sm text-gray-500 text-right mb-4">
          <span className="text-red-500">*</span> Champs obligatoires
        </p>

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-slate font-medium mb-2">
              Connectez-vous avec
            </label>
            <div className="flex border border-gray-300 rounded-md overflow-hidden">
              <button
                type="button"
                className={`flex-1 py-2 px-4 ${formData.contactType === 'email' ? 'bg-olive text-white' : 'bg-gray-100'}`}
                onClick={() => handleContactTypeChange('email')}
              >
                Email
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 ${formData.contactType === 'phone' ? 'bg-olive text-white' : 'bg-gray-100'}`}
                onClick={() => handleContactTypeChange('phone')}
              >
                Téléphone
              </button>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-slate font-medium mb-2" htmlFor="contact">
              {formData.contactType === 'email' ? 'Email' : 'Téléphone (ex: +22990000000)'}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type={formData.contactType === 'email' ? 'email' : 'tel'}
              id="contact"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              placeholder={formData.contactType === 'email' ? 'exemple@gmail.com' : '+22996XXXXXX'}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-slate font-medium mb-2" htmlFor="password">
              Mot de passe <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-olive text-white py-2 px-4 rounded-md hover:bg-terracotta transition duration-300 disabled:opacity-50"
          >
            {loading ? 'Connexion en cours...' : 'Se connecter'}
          </button>
          
          <div className="mt-4 text-right">
            <Link to="/forgot-password" className="text-terracotta hover:underline">
              Mot de passe oublié ?
            </Link>
          </div>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-slate">
            Pas encore de compte? <Link to="/register" className="text-terracotta hover:underline">S'inscrire</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;